import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Container, Table, Button, Form } from "react-bootstrap";
import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";
import { AuthContext } from "../../../Context/AuthContext";
import { toast } from "react-toastify";

const VfcBulkUpload = () => {
  const Api = process.env.REACT_APP_API;
  const { selectedJfmcType } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [vfcs, setVfcs] = useState([]);

  // table-form state (keyed by vfcId)
  const [formData, setFormData] = useState({});
  const [previousDates, setPreviousDates] = useState({});

  /* -------------------- JFMC -------------------- */
  useEffect(() => {
    if (selectedJfmcType) {
      setVfcs([]);
      setFormData({});
      fetchVfcs(selectedJfmcType._id);
    }
  }, [selectedJfmcType]);

  const fetchPreviousDatesBulk = async (vfcIds) => {
    try {
      const config = Authorization();

      const [
        auditRes,
        renewalRes,
        ecRes,
        gbRes,
      ] = await Promise.all([
        axios.post(`${Api}/api/audits/previous-date`, { vfcIds }, config),
        axios.post(`${Api}/api/renewal/renewal-date`, { vfcIds }, config),
        axios.post(`${Api}/api/vfc/previous/ec-meeting`, { vfcIds }, config),
        axios.post(`${Api}/api/vfc/previous/gb-meeting`, { vfcIds }, config),
      ]);

      const map = {};

      // 🟢 Audit
      auditRes.data.audits?.forEach((a) => {
        map[a._id] = { ...(map[a._id] || {}), auditDate: a.auditDate };
      });

      // 🟢 Renewal
      renewalRes.data.renewals?.forEach((r) => {
        map[r._id] = { ...(map[r._id] || {}), renewalDate: r.renewalDate };
      });

      // 🟢 EC Meeting
      ecRes.data.meetings?.forEach((m) => {
        map[m._id] = { ...(map[m._id] || {}), ecMeetingDate: m.ecMeetingDate };
      });

      // 🟢 GB Meeting
      gbRes.data.meetings?.forEach((m) => {
        map[m._id] = { ...(map[m._id] || {}), gbMeetingDate: m.gbMeetingDate };
      });

      setPreviousDates(map);
    } catch (err) {
      console.error("Failed to fetch previous dates (bulk)", err);
    }
  };


  const fetchVfcs = async (jfmcTypeId) => {
    try {
      setLoading(true);
      const config = Authorization();

      const res = await axios.post(
        `${Api}/api/vfcs/names`,
        { jfmcTypeId },
        config
      );

      const vfcList = res.data.vfcs || [];
      setVfcs(vfcList);

      if (vfcList.length) {
        const vfcIds = vfcList.map((v) => v._id);
        fetchPreviousDatesBulk(vfcIds); // ✅ SINGLE CALL
      }
    } catch (err) {
      toast.error("Failed to load VFCs");
    } finally {
      setLoading(false);
    }
  };



  /* -------------------- FORM HANDLER -------------------- */
  const handleChange = (vfcId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [vfcId]: {
        ...prev[vfcId],
        [field]: value,
      },
    }));
  };

  const isSameDate = (d1, d2) => {
    if (!d1 || !d2) return false;

    const a = new Date(d1).toISOString().split("T")[0];
    const b = new Date(d2).toISOString().split("T")[0];

    return a === b;
  };


  /* -------------------- SUBMIT -------------------- */
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const config = Authorization();

      let skippedCount = 0;
      let submittedCount = 0;

      for (const vfcId of Object.keys(formData)) {
        const row = formData[vfcId];
        const prev = previousDates[vfcId] || {};

        /* ---------------- AUDIT ---------------- */
        if (
          row.auditDate &&
          !isSameDate(row.auditDate, prev.auditDate)
        ) {
          await axios.post(
            `${Api}/api/audits`,
            {
              vfcId,
              auditDate: row.auditDate,
              auditorName: "-",
              balanceDuringAudit: 0,
            },
            config
          );
          submittedCount++;
        } else if (row.auditDate) {
          skippedCount++;
        }

        /* ---------------- RENEWAL ---------------- */
        if (
          row.renewalDate &&
          !isSameDate(row.renewalDate, prev.renewalDate)
        ) {
          await axios.post(
            `${Api}/api/renewal`,
            {
              vfcId,
              renewalNo: "-",
              renewalDate: row.renewalDate,
            },
            config
          );
          submittedCount++;
        } else if (row.renewalDate) {
          skippedCount++;
        }

        /* ---------------- EC MEETING ---------------- */
        if (
          row.ecMeetingDate &&
          !isSameDate(row.ecMeetingDate, prev.ecMeetingDate)
        ) {
          await axios.post(
            `${Api}/api/vfc/meetings`,
            {
              vfcId,
              meetingType: "EC Meetings",
              date: row.ecMeetingDate,
              documents: [],
            },
            config
          );
          submittedCount++;
        } else if (row.ecMeetingDate) {
          skippedCount++;
        }

        /* ---------------- GENERAL BODY ---------------- */
        if (
          row.gbMeetingDate &&
          !isSameDate(row.gbMeetingDate, prev.gbMeetingDate)
        ) {
          await axios.post(
            `${Api}/api/vfc/meetings`,
            {
              vfcId,
              meetingType: "General Body",
              date: row.gbMeetingDate,
              documents: [],
            },
            config
          );
          submittedCount++;
        } else if (row.gbMeetingDate) {
          skippedCount++;
        }
      }

      toast.success(
        `Update completed For VFC's. Submitted: ${submittedCount}, Skipped (same date): ${skippedCount}`
      );

      setFormData({});
    } catch (err) {
      console.error(err);
      toast.error("Bulk upload failed");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div>
      {loading && <Preloader loading={loading} />}

      <div className="page-header">
        <h3 className="page-title">VFC Bulk Update</h3>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No JFMC Type Selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <Container>
          <div className="card">
            <div className="card-body">
              <Table bordered responsive>
                <thead>
                  <tr>
                    <th>JFMC Name</th>
                    <th>Last Audit Date</th>
                    <th>Last Renewal Date</th>
                    <th>Last EC Meeting Date</th>
                    <th>Last General Body Date</th>
                  </tr>
                </thead>
                <tbody>
                  {vfcs.map((vfc) => (
                    <tr key={vfc._id}>
                      <td>{vfc.vfcName}</td>

                      <td>
                        {previousDates[vfc._id]?.auditDate && (
                          <span className="text-success fw-bold d-block">
                            Prev:{" "}
                            {new Date(
                              previousDates[vfc._id].auditDate
                            ).toLocaleDateString()}
                          </span>
                        )}

                        <Form.Control
                          type="date"
                          onChange={(e) =>
                            handleChange(vfc._id, "auditDate", e.target.value)
                          }
                        />
                      </td>


                      <td>
                        {previousDates[vfc._id]?.renewalDate && (
                          <span className="text-success fw-bold d-block">
                            Prev:{" "}
                            {new Date(
                              previousDates[vfc._id].renewalDate
                            ).toLocaleDateString()}
                          </span>
                        )}

                        <Form.Control
                          type="date"
                          onChange={(e) =>
                            handleChange(vfc._id, "renewalDate", e.target.value)
                          }
                        />
                      </td>


                      <td>
                        {previousDates[vfc._id]?.ecMeetingDate && (
                          <span className="text-success fw-bold d-block">
                            Prev:{" "}
                            {new Date(
                              previousDates[vfc._id].ecMeetingDate
                            ).toLocaleDateString()}
                          </span>
                        )}

                        <Form.Control
                          type="date"
                          onChange={(e) =>
                            handleChange(vfc._id, "ecMeetingDate", e.target.value)
                          }
                        />
                      </td>


                      <td>
                        {previousDates[vfc._id]?.gbMeetingDate && (
                          <span className="text-success fw-bold d-block">
                            Prev:{" "}
                            {new Date(
                              previousDates[vfc._id].gbMeetingDate
                            ).toLocaleDateString()}
                          </span>
                        )}

                        <Form.Control
                          type="date"
                          onChange={(e) =>
                            handleChange(vfc._id, "gbMeetingDate", e.target.value)
                          }
                        />
                      </td>

                    </tr>
                  ))}
                </tbody>
              </Table>

              <Button variant="primary" onClick={handleSubmit}>
                Save Bulk Data
              </Button>
            </div>
          </div>
        </Container>
      )}
    </div>
  );
};

export default VfcBulkUpload;
